import { apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const SUBMIT_ORDER_URL = 'backoffice/order/submit';

interface SubmitOrderPayload {
  id: string;
}

interface SubmitOrderResponse {
  success: boolean;
  message: string;
}

const submitOrder = async (
  token: string,
  payload: SubmitOrderPayload
): Promise<SubmitOrderResponse> => {
  const response = await apiInstance(token).patch(
    `${SUBMIT_ORDER_URL}/${payload.id}`,
    JSON.stringify({ id: payload.id })
  );
  return response.data;
};

export const handleSubmitOrderError = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const errorCode = error.response?.data?.error;
    if (typeof errorCode === 'string' && errorCode.startsWith('SAP_')) {
      return error.response?.data?.message || errorCode;
    }
    return (
      error.response?.data?.message || 'Terjadi kesalahan saat submit order'
    );
  }
  return 'Terjadi kesalahan yang tidak diketahui';
};

export const useActivityMutations = () => {
  const token = useAuthStore((s) => s.access_token);
  const queryClient = useQueryClient();

  const submitOrderMutation = useMutation({
    mutationFn: (payload: SubmitOrderPayload) => submitOrder(token, payload),
    onSuccess: () => {
      // Invalidate and refetch activities list
      queryClient.invalidateQueries({ queryKey: ['Activities'] });
    },
    onError: (error) => {
      // Error is handled by the component using the mutation
      console.error('Submit order error:', error);
    },
  });

  const submitMultipleOrdersMutation = useMutation({
    mutationFn: async (payloads: SubmitOrderPayload[]) => {
      const results = await Promise.allSettled(
        payloads.map((payload) => submitOrder(token, payload))
      );

      const successful = results.filter(
        (result): result is PromiseFulfilledResult<SubmitOrderResponse> =>
          result.status === 'fulfilled'
      );

      const failed = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected'
      );

      return {
        successful: successful.length,
        failed: failed.length,
        total: payloads.length,
        errors: failed.map((result) => handleSubmitOrderError(result.reason)),
      };
    },
    onSuccess: () => {
      // Invalidate and refetch activities list
      queryClient.invalidateQueries({ queryKey: ['Activities'] });
    },
  });

  return {
    submitOrder: submitOrderMutation.mutateAsync,
    submitMultipleOrders: submitMultipleOrdersMutation.mutateAsync,
    isSubmitting: submitOrderMutation.isPending,
    isSubmittingMultiple: submitMultipleOrdersMutation.isPending,
    submitError: submitOrderMutation.error,
    submitMultipleError: submitMultipleOrdersMutation.error,
  };
};
